import { memo, useEffect, useMemo } from "react";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { LngLatBounds } from "maplibre-gl";
import * as Turf from "@turf/turf";
import { currentProjectAtom } from "~/stores/ProjectStore";
import { CommentAPIs, Comment_GetPage_RequestQuery } from "~/common/typescript/apis/CommentAPIs";
import { CommentMapUtilOptimized } from "./CommentMapUtilOptimized";
import {
  commentDisplayedBoundsAtom,
  commentsAtom,
  focusedCommentIdAtom,
  showCommentLabelsAtom,
  showCommentOnMapAtom,
} from "./CommentStore";
import { isMapLoadedDoneAtom, showCommentPanelAtom } from "../stores/mapStore";
import { CommentItemUtil } from "../panels/CommentPanel/CommentItem/CommentItemUtil";

const CommentMapController = () => {
  const [showCommentOnMap, setShowCommentOnMap] = useAtom(showCommentOnMapAtom);
  const setShowCommentPanel = useSetAtom(showCommentPanelAtom);
  const setFocusedCommentId = useSetAtom(focusedCommentIdAtom);
  const [currentProject] = useAtom(currentProjectAtom);
  const [comments, setComments] = useAtom(commentsAtom);
  const [isMapLoadedDone] = useAtom(isMapLoadedDoneAtom);
  const setCommentDisplayedBounds = useSetAtom(commentDisplayedBoundsAtom);
  const showCommentLabels = useAtomValue(showCommentLabelsAtom);

  const filteredComments = useMemo(
    () => comments.filter((comment) => !comment.is_deleted),
    [comments],
  );

  useEffect(() => {
    if (!showCommentOnMap) {
      CommentMapUtilOptimized.removeCommentsFromMap(filteredComments);
    }
  }, [showCommentOnMap, filteredComments]);

  useEffect(() => {
    if (!isMapLoadedDone || !showCommentOnMap) return;

    CommentMapUtilOptimized.updateComments(filteredComments, showCommentLabels);

    let combinedCommentBounds: LngLatBounds | null = null;
    filteredComments.forEach((comment) => {
      if (!comment.geojson) return;
      try {
        const geojsonObj = JSON.parse(comment.geojson);
        const bbox = Turf.bbox(geojsonObj);
        const commentBounds = new LngLatBounds([bbox[0], bbox[1]], [bbox[2], bbox[3]]);
        if (combinedCommentBounds) {
          combinedCommentBounds.extend(commentBounds);
        } else {
          combinedCommentBounds = commentBounds;
        }
      } catch (error) {
        console.error("Error parsing geojson for comment:", comment.id, error);
      }
    });

    if (combinedCommentBounds) {
      const bounds = combinedCommentBounds as LngLatBounds;
      setCommentDisplayedBounds(
        JSON.stringify([
          [bounds.getWest(), bounds.getSouth()],
          [bounds.getEast(), bounds.getNorth()],
        ]),
      );
    } else {
      setCommentDisplayedBounds(null);
    }

    const onClick = (e: any) => {
      const item = e.features?.[0];
      const commentId = Number(item?.properties?.id);
      if (!commentId) return;

      setShowCommentPanel(true);
      setFocusedCommentId(commentId);
      window.dispatchEvent(
        new CustomEvent("geocloud:scroll-to-comment", { detail: { commentId } }),
      );
    };

    const onDoubleClick = (e: any) => {
      if (e.features?.[0]) {
        e.preventDefault();
      }
    };

    window.map?.on("click", CommentMapUtilOptimized._layerId, onClick);
    window.map?.on("dblclick", CommentMapUtilOptimized._layerId, onDoubleClick);

    return () => {
      window.map?.off("click", CommentMapUtilOptimized._layerId, onClick);
      window.map?.off("dblclick", CommentMapUtilOptimized._layerId, onDoubleClick);
    };
  }, [
    isMapLoadedDone,
    filteredComments,
    showCommentOnMap,
    showCommentLabels,
    setCommentDisplayedBounds,
    setShowCommentPanel,
    setFocusedCommentId,
  ]);

  // Listen for 'geocloud:scroll-to-comment' (from clicking a comment pin or label overlay on map)
  useEffect(() => {
    const handleSelectFromMap = (event: Event) => {
      const customEvent = event as CustomEvent<{ commentId?: number }>;
      const commentId = Number(customEvent.detail?.commentId);
      if (!commentId) return;
      setShowCommentPanel(true);
      setFocusedCommentId(commentId);
    };

    window.addEventListener("geocloud:scroll-to-comment", handleSelectFromMap);
    return () => {
      window.removeEventListener("geocloud:scroll-to-comment", handleSelectFromMap);
    };
  }, [setShowCommentPanel, setFocusedCommentId]);

  // Listen for 'geocloud:focus-comment' event from NotificationBell deep-link
  useEffect(() => {
    const handleFocusComment = (event: Event) => {
      const customEvent = event as CustomEvent<{ commentId?: number; geojson?: string }>;
      const targetId = Number(customEvent.detail?.commentId);
      if (!targetId) return;

      setShowCommentOnMap(true);
      setShowCommentPanel(true);
      setFocusedCommentId(targetId);

      const targetComment = filteredComments.find((c) => Number(c.id) === targetId);
      if (targetComment) {
        CommentItemUtil.zoomToComment(targetComment);
      }

      window.dispatchEvent(
        new CustomEvent("geocloud:scroll-to-comment", { detail: { commentId: targetId } }),
      );
    };

    window.addEventListener("geocloud:focus-comment", handleFocusComment);
    return () => {
      window.removeEventListener("geocloud:focus-comment", handleFocusComment);
    };
  }, [filteredComments, setShowCommentOnMap, setShowCommentPanel, setFocusedCommentId]);

  useEffect(() => {
    if (!currentProject || !isMapLoadedDone) return;

    const payload: Comment_GetPage_RequestQuery = {
      projectId: currentProject.id as number,
      page: 1,
      pageSize: 100,
    };
    CommentAPIs.remote_getPage(payload).then((res) => {
      setComments(res.results);
    });
  }, [isMapLoadedDone, currentProject, setComments]);

  return null;
};

export default memo(CommentMapController);
